import { HistorySection } from './EntityHistory'
import { Badge, taskStatusVariant } from './ui/Badge'
import { Drawer } from './ui/Drawer'

// Full shape of any agile entity returned by /api/agile/tree (drizzle .select()).
export type AgileEntity = {
  id: string
  title: string
  description?: string | null
  status: string
  priority?: string
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
  version: number
  createdBy?: string | null
  updatedBy?: string | null
  // Feature-only
  projectId?: string | null
  clientId?: string | null
  targetDate?: string | null
  completedAt?: string | null
  // User story-only
  featureId?: string
  acceptanceCriteria?: string | null
  storyPoints?: number | null
  // Task-only
  userStoryId?: string
  assignedAgentType?: string | null
  assignedUserId?: string | null
  automationLevel?: 'green' | 'yellow' | 'red' | null
  estimatedHours?: number | null
  spentHours?: number | null
  keywords?: string[]
  dueDate?: string | null
  branch?: string | null
}

type EntityKind = 'feature' | 'user_story' | 'task'

const KIND_LABEL: Record<EntityKind, string> = {
  feature: 'Feature',
  user_story: 'User Story',
  task: 'Task',
}

function formatDate(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' })
}

function formatDateOnly(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('it-IT', { dateStyle: 'medium' })
}

function automationBadge(level?: string | null) {
  if (!level) return null
  const variant = level === 'green' ? 'success' : level === 'yellow' ? 'warning' : 'danger'
  const label =
    level === 'green'
      ? 'verde · autonomo'
      : level === 'yellow'
        ? 'giallo · approva'
        : 'rosso · ferma'
  return <Badge variant={variant}>{label}</Badge>
}

export function AgileDetailDrawer({
  entity,
  kind,
  context,
  onClose,
}: {
  entity: AgileEntity | null
  kind: EntityKind
  context?: {
    featureTitle?: string
    userStoryTitle?: string
    projectName?: string
    clientName?: string
  }
  onClose: () => void
}) {
  if (!entity) return null

  const kindLabel = KIND_LABEL[kind]
  const breadcrumb: string[] = []
  if (context?.clientName) breadcrumb.push(context.clientName)
  if (context?.projectName) breadcrumb.push(context.projectName)
  if (kind !== 'feature' && context?.featureTitle) breadcrumb.push(context.featureTitle)
  if (kind === 'task' && context?.userStoryTitle) breadcrumb.push(context.userStoryTitle)

  return (
    <Drawer
      open={!!entity}
      onClose={onClose}
      title={entity.title}
      subtitle={
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <span className="uppercase tracking-wider">{kindLabel}</span>
            <span className="text-zinc-600">·</span>
            <span className="font-mono">#{entity.id.slice(0, 8)}</span>
            <span className="text-zinc-600">·</span>
            <span>v{entity.version}</span>
          </div>
          {breadcrumb.length > 0 && (
            <div className="text-xs text-zinc-500 truncate">{breadcrumb.join(' › ')}</div>
          )}
        </div>
      }
      badges={
        <>
          <Badge variant={taskStatusVariant(entity.status)}>
            {entity.status.replace(/_/g, ' ')}
          </Badge>
          {entity.priority && entity.priority !== 'medium' && (
            <Badge
              variant={
                entity.priority === 'urgent'
                  ? 'danger'
                  : entity.priority === 'high'
                    ? 'warning'
                    : 'muted'
              }
            >
              {entity.priority}
            </Badge>
          )}
          {automationBadge(entity.automationLevel)}
        </>
      }
      width="md"
    >
      <div className="p-5 space-y-6">
        {/* Description */}
        {entity.description && (
          <section>
            <SectionTitle>Descrizione</SectionTitle>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{entity.description}</p>
          </section>
        )}

        {/* Feature-specific */}
        {kind === 'feature' && (entity.targetDate || entity.completedAt) && (
          <section>
            <SectionTitle>Date</SectionTitle>
            <Grid>
              {entity.targetDate && (
                <KeyVal k="Target date" v={formatDateOnly(entity.targetDate)} />
              )}
              {entity.completedAt && (
                <KeyVal k="Completata il" v={formatDate(entity.completedAt)} />
              )}
            </Grid>
          </section>
        )}

        {/* User Story-specific */}
        {kind === 'user_story' && entity.acceptanceCriteria && (
          <section>
            <SectionTitle>Acceptance criteria</SectionTitle>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{entity.acceptanceCriteria}</p>
          </section>
        )}
        {kind === 'user_story' && entity.storyPoints != null && (
          <section>
            <SectionTitle>Stima</SectionTitle>
            <Grid>
              <KeyVal k="Story points" v={String(entity.storyPoints)} />
            </Grid>
          </section>
        )}

        {/* Task-specific */}
        {kind === 'task' && (
          <section>
            <SectionTitle>Assegnazione & esecuzione</SectionTitle>
            <Grid>
              {entity.assignedAgentType ? (
                <KeyVal k="Agente" v={entity.assignedAgentType} />
              ) : entity.assignedUserId ? (
                <KeyVal k="Utente" v={`#${entity.assignedUserId.slice(0, 8)}`} />
              ) : (
                <KeyVal k="Assegnazione" v="non assegnato" />
              )}
              {entity.automationLevel && <KeyVal k="Automation level" v={entity.automationLevel} />}
              {entity.estimatedHours != null && (
                <KeyVal k="Ore stimate" v={`${entity.estimatedHours}h`} />
              )}
              {entity.spentHours != null && entity.spentHours > 0 && (
                <KeyVal k="Ore consuntivate" v={`${entity.spentHours}h`} />
              )}
              {entity.dueDate && <KeyVal k="Scadenza" v={formatDateOnly(entity.dueDate)} />}
              {entity.branch && <KeyVal k="Branch" v={entity.branch} />}
            </Grid>
          </section>
        )}

        {kind === 'task' && entity.keywords && entity.keywords.length > 0 && (
          <section>
            <SectionTitle>
              Keyword{' '}
              <span className="text-zinc-500 normal-case font-normal">
                (per matching L2 TimeTrack)
              </span>
            </SectionTitle>
            <div className="flex flex-wrap gap-1.5">
              {entity.keywords.map((k) => (
                <span
                  key={k}
                  className="rounded bg-zinc-800/60 border border-zinc-700 px-2 py-0.5 text-xs text-zinc-300"
                >
                  {k}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Audit */}
        <section>
          <SectionTitle>Audit</SectionTitle>
          <Grid>
            <KeyVal k="Creato" v={formatDate(entity.createdAt)} />
            <KeyVal k="Aggiornato" v={formatDate(entity.updatedAt)} />
            <KeyVal k="Versione" v={`v${entity.version}`} />
            {entity.createdBy && <KeyVal k="Created by" v={`#${entity.createdBy.slice(0, 8)}`} />}
            {entity.updatedBy && <KeyVal k="Updated by" v={`#${entity.updatedBy.slice(0, 8)}`} />}
          </Grid>
        </section>

        {/* History */}
        <HistorySection entityType={kind} entityId={entity.id} />

        {/* Metadata raw (filtered: hide noisy migration fields) */}
        {(() => {
          if (!entity.metadata) return null
          const NOISE_KEYS = new Set(['migratedFrom', 'oldId'])
          const filtered = Object.fromEntries(
            Object.entries(entity.metadata).filter(([k]) => !NOISE_KEYS.has(k)),
          )
          if (Object.keys(filtered).length === 0) return null
          return (
            <section>
              <SectionTitle>Metadata</SectionTitle>
              <pre className="rounded-md bg-zinc-950/80 border border-zinc-800 p-3 text-xs text-zinc-300 overflow-x-auto">
                {JSON.stringify(filtered, null, 2)}
              </pre>
            </section>
          )
        })()}
      </div>
    </Drawer>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
      {children}
    </h3>
  )
}

function Grid({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">{children}</dl>
}

function KeyVal({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-zinc-500">{k}</dt>
      <dd className="text-zinc-200 truncate">{v}</dd>
    </div>
  )
}
